// BroadcastPage — dispatcher composes a custom push notification, picks an
// audience, sees a live reach estimate, confirms, and broadcasts. History
// of past broadcasts shown below.
//
// Reuses backend endpoints:
//   GET  /api/push/broadcast/reach?audience=...   → { subscribers }
//   POST /api/push/broadcast                       → { ok, broadcast_id, stats, sent_at }
//   GET  /api/push/broadcasts?limit=50             → { broadcasts: [...] }

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useFlag } from '../../context/FeatureFlagsContext';
import { useSSE } from '../../hooks/useSSE';
import { Navbar } from '../../components/Navbar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Send, Users, BellRing, Megaphone, Truck, ShieldCheck, Clock, X,
  FileText, Save, Trash2, ChevronDown, Radio, FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const AUDIENCES = [
  { key: 'customer',   label: 'Customers',   Icon: Users,       suggestUrl: '/customer' },
  { key: 'driver',     label: 'Drivers',     Icon: Truck,       suggestUrl: '/driver' },
  { key: 'dispatcher', label: 'Dispatchers', Icon: ShieldCheck, suggestUrl: '/dispatch' },
  { key: 'all',        label: 'Everyone',    Icon: Megaphone,   suggestUrl: '/' },
];

const TITLE_MAX = 80;
const BODY_MAX = 240;

export function BroadcastPage() {
  const { user } = useAuth();
  const broadcastEnabled = useFlag('dispatcher.broadcast', true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('customer');
  const [url, setUrl] = useState('/customer');
  const [sendMode, setSendMode] = useState('now');           // 'now' | 'schedule'
  const [scheduledLocal, setScheduledLocal] = useState(''); // datetime-local value
  const [reach, setReach] = useState(null);
  const [reachLoading, setReachLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [sseConnected, setSseConnected] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchReach = useCallback(async (aud) => {
    setReachLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/push/broadcast/reach?audience=${aud}`,
        { withCredentials: true }
      );
      setReach(data?.subscribers ?? 0);
    } catch (_) {
      setReach(null);
    } finally {
      setReachLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/push/broadcasts?limit=50`,
        { withCredentials: true }
      );
      setHistory(data?.broadcasts || []);
    } catch (_) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/push/broadcast/templates`,
        { withCredentials: true }
      );
      setTemplates(data?.templates || []);
    } catch (_) {
      setTemplates([]);
    }
  }, []);

  // SSE: refresh history (and templates) in real time when anything changes
  useSSE(useCallback((evt) => {
    if (!evt) return;
    if (evt.type === 'stream.ready') { setSseConnected(true); return; }
    if (evt.type === 'broadcast.created' || evt.type === 'broadcast.cancelled' || evt.type === 'broadcast.fired') {
      if (evt.type === 'broadcast.fired') {
        toast.info('Scheduled broadcast fired', { duration: 4000 });
      }
      fetchHistory();
    }
    if (evt.type === 'broadcast.template_changed') {
      fetchTemplates();
    }
  }, [fetchHistory, fetchTemplates]));

  useEffect(() => { fetchReach(audience); }, [audience, fetchReach]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Auto-suggest URL on audience change (if user hasn't manually overridden)
  useEffect(() => {
    const a = AUDIENCES.find(x => x.key === audience);
    if (a) setUrl(a.suggestUrl);
  }, [audience]);

  const canSend = title.trim() && body.trim() && audience && !sending && broadcastEnabled
    && (sendMode === 'now' || (sendMode === 'schedule' && scheduledLocal));

  const scheduledIsoUtc = () => {
    if (sendMode !== 'schedule' || !scheduledLocal) return null;
    // datetime-local produces "YYYY-MM-DDTHH:MM" in local time; convert to UTC ISO
    const d = new Date(scheduledLocal);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        audience,
        url: url.trim() || undefined,
      };
      const iso = scheduledIsoUtc();
      if (iso) payload.scheduled_at = iso;
      const { data } = await axios.post(
        `${API_URL}/api/push/broadcast`,
        payload,
        { withCredentials: true }
      );
      if (data?.status === 'scheduled') {
        toast.success(`Scheduled for ${new Date(data.scheduled_at).toLocaleString()}`, { duration: 6000 });
      } else {
        const stats = data?.stats || {};
        toast.success(
          `Sent ${stats.sent ?? 0} • Cleaned ${stats.removed ?? 0} • Failed ${stats.failed ?? 0}`,
          { duration: 6000 }
        );
      }
      setConfirmOpen(false);
      setTitle('');
      setBody('');
      setScheduledLocal('');
      setSendMode('now');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  const cancelBroadcast = async (id) => {
    if (!window.confirm('Cancel this scheduled broadcast?')) return;
    try {
      await axios.delete(`${API_URL}/api/push/broadcasts/${id}`, { withCredentials: true });
      toast.info('Scheduled broadcast cancelled');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    }
  };

  const loadTemplate = (t) => {
    setTitle(t.title || '');
    setBody(t.body || '');
    setAudience(t.audience || 'customer');
    if (t.url) setUrl(t.url);
    setTemplatesOpen(false);
    toast.success(`Loaded "${t.name}"`);
  };

  const saveTemplate = async () => {
    if (!templateName.trim() || !title.trim() || !body.trim()) {
      toast.error('Name, title and body are required');
      return;
    }
    setSavingTemplate(true);
    try {
      await axios.post(
        `${API_URL}/api/push/broadcast/templates`,
        {
          name: templateName.trim(),
          title: title.trim(),
          body: body.trim(),
          audience,
          url: url.trim() || undefined,
        },
        { withCredentials: true }
      );
      toast.success(`Template "${templateName}" saved`);
      setTemplateName('');
      setSaveTemplateOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (t, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/push/broadcast/templates/${t.id}`, { withCredentials: true });
      toast.info(`Template "${t.name}" deleted`);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const testSendToSelf = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body required to preview');
      return;
    }
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/push/broadcast/test-self`,
        { title: title.trim(), body: body.trim(), audience, url: url.trim() || undefined },
        { withCredentials: true }
      );
      const stats = data?.stats || {};
      if ((stats.sent ?? 0) > 0) {
        toast.success(`Test sent to ${stats.sent} of your device(s). Check your notifications.`);
      } else {
        toast.warning('No active push subscriptions on your account yet — click ENABLE PUSH on the dispatch dashboard first.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  if (!broadcastEnabled) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-12">
          <p className="text-[#FF9F0A] font-mono">// BROADCAST FEATURE DISABLED — toggle dispatcher.broadcast in /dispatch/settings</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top" data-testid="broadcast-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <Link to="/dispatch" className="inline-flex items-center text-[#4B6B52] hover:text-[#39FF14] font-mono text-sm mb-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> BACK TO DISPATCH
            </Link>
            <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
              {'>'} BROADCAST_COMPOSER
            </h1>
            <p className="text-[#4B6B52] font-mono text-sm mt-1">
              // SENT BY {user?.email?.toUpperCase() || 'DISPATCHER'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`flex items-center gap-2 px-3 py-1 border font-mono text-xs uppercase ${
                sseConnected
                  ? 'border-[#39FF14] text-[#39FF14] bg-[rgba(57,255,20,0.08)]'
                  : 'border-[#FF9F0A] text-[#FF9F0A] bg-[rgba(255,159,10,0.08)]'
              }`}
              data-testid="broadcast-sse-status"
              title={sseConnected ? 'Real-time stream connected' : 'Connecting to real-time stream...'}
            >
              <Radio className={`w-3 h-3 ${sseConnected ? 'animate-pulse' : ''}`} />
              {sseConnected ? 'LIVE' : 'CONNECTING'}
            </span>
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setTemplatesOpen(v => !v)}
                className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono"
                data-testid="templates-menu-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                TEMPLATES ({templates.length})
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {templatesOpen && (
                <div
                  className="absolute right-0 mt-1 w-80 max-h-96 overflow-auto bg-[#0C0612] border border-[#39FF14] z-50 shadow-lg"
                  data-testid="templates-dropdown"
                >
                  {templates.length === 0 ? (
                    <p className="text-[#4B6B52] font-mono text-xs p-3">// no templates saved yet</p>
                  ) : (
                    templates.map(t => (
                      <div
                        key={t.id}
                        onClick={() => loadTemplate(t)}
                        className="p-3 border-b border-[#2A0F3D] last:border-0 hover:bg-[rgba(57,255,20,0.06)] cursor-pointer flex items-start justify-between gap-2"
                        data-testid={`template-row-${t.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-[#39FF14] font-mono text-xs font-bold truncate">{t.name}</p>
                          <p className="text-[#A3C7A8] font-mono text-[10px] mt-1 truncate">{t.title}</p>
                          <p className="text-[#4B6B52] font-mono text-[10px] mt-1 uppercase">{t.audience}</p>
                        </div>
                        <button
                          onClick={(e) => deleteTemplate(t, e)}
                          className="flex-shrink-0 text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] p-1 rounded-none"
                          data-testid={`template-delete-${t.id}`}
                          title="Delete template"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compose form */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6 mb-8">
          <p className="text-[#39FF14] font-mono text-sm font-bold uppercase mb-4">// COMPOSE</p>

          {/* Audience picker */}
          <div className="mb-5">
            <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-2">AUDIENCE</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {AUDIENCES.map(a => {
                const Icon = a.Icon;
                const active = audience === a.key;
                return (
                  <button
                    key={a.key}
                    onClick={() => setAudience(a.key)}
                    className={`flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase rounded-none transition-colors ${
                      active
                        ? 'border-[#39FF14] bg-[rgba(57,255,20,0.08)] text-[#39FF14]'
                        : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14]'
                    }`}
                    data-testid={`audience-${a.key}`}
                  >
                    <Icon className="w-3 h-3" />
                    {a.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[#4B6B52] font-mono text-xs mt-2" data-testid="reach-estimate">
              {reachLoading ? '// counting subscribers...' : reach === null
                ? '// reach unavailable'
                : `// estimated reach: ${reach} subscribed device${reach === 1 ? '' : 's'}`}
            </p>
          </div>

          {/* Title */}
          <div className="mb-4">
            <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-2">TITLE ({title.length}/{TITLE_MAX})</p>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
              placeholder="20% OFF Saturday"
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
              data-testid="broadcast-title"
            />
          </div>

          {/* Body */}
          <div className="mb-4">
            <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-2">BODY ({body.length}/{BODY_MAX})</p>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
              placeholder="Use code SATURDAY20 — Blue Dream and edibles all day. Tap to shop."
              rows={3}
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none resize-none"
              data-testid="broadcast-body"
            />
          </div>

          {/* URL */}
          <div className="mb-4">
            <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-2">CLICK DESTINATION</p>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/customer"
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
              data-testid="broadcast-url"
            />
          </div>

          {/* When to send */}
          <div className="mb-6">
            <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-2">WHEN</p>
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setSendMode('now')}
                className={`flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase rounded-none transition-colors ${
                  sendMode === 'now'
                    ? 'border-[#39FF14] bg-[rgba(57,255,20,0.08)] text-[#39FF14]'
                    : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14]'
                }`}
                data-testid="send-mode-now"
              >
                <Send className="w-3 h-3" />
                SEND NOW
              </button>
              <button
                onClick={() => setSendMode('schedule')}
                className={`flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase rounded-none transition-colors ${
                  sendMode === 'schedule'
                    ? 'border-[#39FF14] bg-[rgba(57,255,20,0.08)] text-[#39FF14]'
                    : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14]'
                }`}
                data-testid="send-mode-schedule"
              >
                <Clock className="w-3 h-3" />
                SCHEDULE
              </button>
            </div>
            {sendMode === 'schedule' && (
              <>
                <Input
                  type="datetime-local"
                  value={scheduledLocal}
                  onChange={(e) => setScheduledLocal(e.target.value)}
                  min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono focus:border-[#39FF14] rounded-none max-w-xs"
                  data-testid="broadcast-scheduled-at"
                />
                {/* Phase 6.1.0 — peak-time chips. Industry-average open rates
                    for hemp/CBD e-commerce. Personalized to operator data in
                    Phase 6.1.2 once we have 30+ days of email_events. */}
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="broadcast-peak-chips">
                  <span className="text-[#6B8E73] text-[10px] mr-1 self-center font-mono">// peak times:</span>
                  {[
                    { label: 'Fri 5pm', wday: 5, hr: 17, tip: '~48% open rate' },
                    { label: 'Thu 11am', wday: 4, hr: 11, tip: '~42% open rate' },
                    { label: 'Sat 10am', wday: 6, hr: 10, tip: '~40% open rate' },
                  ].map((c) => (
                    <button
                      key={c.label}
                      type="button"
                      onClick={() => {
                        const now = new Date();
                        const d = new Date(now);
                        const diff = (c.wday + 7 - d.getDay()) % 7;
                        d.setDate(d.getDate() + (diff === 0 && now.getHours() >= c.hr ? 7 : diff));
                        d.setHours(c.hr, 0, 0, 0);
                        const pad = (n) => String(n).padStart(2, '0');
                        setScheduledLocal(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                      }}
                      className="border border-[#39FF14] text-[#39FF14] hover:bg-[rgba(57,255,20,0.08)] text-[10px] font-mono px-2 py-1 uppercase tracking-wider"
                      title={c.tip}
                      data-testid={`broadcast-peak-chip-${c.label.replace(/\s+/g,'-').toLowerCase()}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <p className="text-[#4B6B52] font-mono text-xs mt-2">
                  // local time — backend converts to UTC. Min lead: 60s.
                </p>
              </>
            )}
          </div>

          {/* Live preview */}
          <div className="mb-6">
            <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-2">PREVIEW</p>
            <div className="bg-[#050003] border border-[#39FF14] p-3 max-w-md" data-testid="broadcast-preview">
              <div className="flex items-start gap-3">
                <BellRing className="w-5 h-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-[#39FF14] font-mono text-sm font-bold truncate">
                    {title || '(title preview)'}
                  </p>
                  <p className="text-[#A3C7A8] font-mono text-xs mt-1 break-words">
                    {body || '(body preview)'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Send button */}
          <div className="flex justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={testSendToSelf}
              disabled={!title.trim() || !body.trim() || testing}
              className="border-[#00F0FF] text-[#00F0FF] hover:bg-[rgba(0,240,255,0.08)] rounded-none font-mono disabled:opacity-40"
              data-testid="test-send-self-btn"
              title="Send this draft only to your own subscriptions so you can preview the real notification"
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              {testing ? 'SENDING...' : 'TEST SEND TO ME'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setSaveTemplateOpen(true)}
              disabled={!title.trim() || !body.trim()}
              className="border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono disabled:opacity-40"
              data-testid="save-template-btn"
              title="Save current compose as a reusable template"
            >
              <Save className="w-4 h-4 mr-2" />
              SAVE AS TEMPLATE
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!canSend}
              className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] disabled:opacity-40 rounded-none"
              data-testid="broadcast-send-btn"
            >
              {sendMode === 'schedule' ? <Clock className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {sendMode === 'schedule' ? 'REVIEW & SCHEDULE' : 'REVIEW & SEND'}
            </Button>
          </div>
        </div>

        {/* History */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
          <p className="text-[#39FF14] font-mono text-sm font-bold uppercase mb-4">// HISTORY (last 50)</p>
          {historyLoading ? (
            <p className="text-[#4B6B52] font-mono text-sm">// loading...</p>
          ) : history.length === 0 ? (
            <p className="text-[#4B6B52] font-mono text-sm">// no broadcasts sent yet</p>
          ) : (
            <div className="space-y-2" data-testid="broadcast-history">
              {history.map(b => {
                const isScheduled = b.status === 'scheduled';
                const isCancelled = b.status === 'cancelled';
                const primaryTs = isScheduled
                  ? (b.scheduled_at && `scheduled ${new Date(b.scheduled_at).toLocaleString()}`)
                  : (b.sent_at && new Date(b.sent_at).toLocaleString())
                    || (b.created_at && new Date(b.created_at).toLocaleString());
                const rowBorder = isScheduled
                  ? 'border-[#00F0FF] hover:border-[#39FF14]'
                  : isCancelled
                    ? 'border-[#4B6B52] opacity-70'
                    : 'border-[#2A0F3D] hover:border-[#39FF14]';
                return (
                  <div key={b.id} className={`border p-3 transition-colors ${rowBorder}`} data-testid={`broadcast-row-${b.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[#39FF14] font-mono text-sm font-bold">{b.title}</p>
                          {isScheduled && (
                            <span className="px-1.5 py-0.5 border border-[#00F0FF] text-[#00F0FF] font-mono text-[10px] uppercase">
                              <Clock className="inline w-2.5 h-2.5 mr-1" />SCHEDULED
                            </span>
                          )}
                          {isCancelled && (
                            <span className="px-1.5 py-0.5 border border-[#FF3B30] text-[#FF3B30] font-mono text-[10px] uppercase">
                              CANCELLED
                            </span>
                          )}
                        </div>
                        <p className="text-[#A3C7A8] font-mono text-xs mt-1 break-words">{b.body}</p>
                        <p className="text-[#4B6B52] font-mono text-[10px] mt-2 uppercase">
                          {primaryTs} • {b.audience} • by {b.sent_by}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                        {!isScheduled && !isCancelled && (
                          <>
                            <p className="text-[#39FF14] font-mono text-xs">SENT {b.stats?.sent ?? 0}</p>
                            <p className="text-[#FF9F0A] font-mono text-[10px]">CLEAN {b.stats?.removed ?? 0}</p>
                            <p className="text-[#FF3B30] font-mono text-[10px]">FAIL {b.stats?.failed ?? 0}</p>
                          </>
                        )}
                        {isScheduled && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => cancelBroadcast(b.id)}
                            className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] rounded-none font-mono text-[10px] h-7"
                            data-testid={`broadcast-cancel-${b.id}`}
                          >
                            <X className="w-3 h-3 mr-1" /> CANCEL
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Save Template Dialog */}
      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent className="bg-[#0C0612] border-[#39FF14] rounded-none max-w-md font-mono">
          <DialogHeader>
            <DialogTitle className="text-[#39FF14] font-mono uppercase">// SAVE AS TEMPLATE</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-2">TEMPLATE NAME</p>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value.slice(0, 60))}
                placeholder="e.g. Service paused, Restock alert"
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                data-testid="template-name-input"
                maxLength={60}
              />
              <p className="text-[#4B6B52] font-mono text-xs mt-1">// ({templateName.length}/60)</p>
            </div>
            <div className="bg-[#050003] border border-[#2A0F3D] p-3 text-xs">
              <p className="text-[#39FF14] font-mono font-bold">{title || '(no title)'}</p>
              <p className="text-[#A3C7A8] font-mono mt-1">{body || '(no body)'}</p>
              <p className="text-[#4B6B52] font-mono mt-2 uppercase">Audience: {audience}</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSaveTemplateOpen(false)}
              className="border-[#2A0F3D] text-[#A3C7A8] hover:text-[#FF3B30] hover:border-[#FF3B30] rounded-none font-mono"
              data-testid="save-template-cancel-btn"
            >
              CANCEL
            </Button>
            <Button
              onClick={saveTemplate}
              disabled={savingTemplate || !templateName.trim()}
              className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] disabled:opacity-40 rounded-none"
              data-testid="save-template-confirm-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingTemplate ? 'SAVING...' : 'SAVE'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#0C0612] border-[#39FF14] rounded-none max-w-md font-mono">
          <DialogHeader>
            <DialogTitle className="text-[#39FF14] font-mono uppercase">
              // {sendMode === 'schedule' ? 'CONFIRM SCHEDULE' : 'CONFIRM BROADCAST'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#A3C7A8]">
            {sendMode === 'schedule' ? (
              <p>
                Scheduling for <span className="text-[#00F0FF] font-bold">{scheduledLocal && new Date(scheduledLocal).toLocaleString()}</span>
                {' '}— will fan out to <span className="text-[#39FF14] font-bold">{audience.toUpperCase()}</span>{' '}
                (~<span className="text-[#39FF14] font-bold">{reach ?? '?'}</span> device{reach === 1 ? '' : 's'} at current count).
              </p>
            ) : (
              <p>Sending to <span className="text-[#39FF14] font-bold">{audience.toUpperCase()}</span> — approx <span className="text-[#39FF14] font-bold">{reach ?? '?'}</span> device{reach === 1 ? '' : 's'}.</p>
            )}
            <div className="bg-[#050003] border border-[#2A0F3D] p-3">
              <p className="text-[#39FF14] text-sm font-bold">{title}</p>
              <p className="text-[#A3C7A8] text-xs mt-1">{body}</p>
            </div>
            <p className="text-[#FF9F0A] text-xs">
              // {sendMode === 'schedule' ? 'YOU CAN CANCEL BEFORE IT FIRES' : 'THIS CANNOT BE UNDONE'}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              className="border-[#2A0F3D] text-[#A3C7A8] hover:text-[#FF3B30] hover:border-[#FF3B30] rounded-none font-mono"
              data-testid="broadcast-cancel-btn"
            >
              CANCEL
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending}
              className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none"
              data-testid="broadcast-confirm-btn"
            >
              {sendMode === 'schedule' ? <Clock className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {sending
                ? (sendMode === 'schedule' ? 'SCHEDULING...' : 'SENDING...')
                : (sendMode === 'schedule' ? 'SCHEDULE NOW' : 'SEND NOW')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BroadcastPage;
